import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

import { ErrorComponent } from "@components/ErrorComponent";
import { AppNav } from "@components/Header/AppNav";
import { FutureMoneyLogo } from "@components/Header/FutureMoneyLogo";
import { Loader } from "@components/Loader";
import { NODE_COUNT } from "@constants";
import { GoalsContextProvider } from "@contexts";
import { BeneficiaryType, Goal, Routes } from "@ctypes";
import {
  useGoalDetailsPrefetch,
  useGoalsContext,
  usePortfolioPerformanceGraph,
  useTheme,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { Content, Eyebrow, GhostButton, Grain, Page } from "@routes/DashboardRoute/Home/styles";
import { home, homeCssVars } from "@routes/DashboardRoute/Home/tokens";
import {
  calculateContributeAfter18Value,
  calculateMaxGoalLength,
  calculateMaxMultiplier,
  calculateTotalAnnualBeneficiaryDeposit,
  deriveGoalStanding,
  formatCurrency,
} from "@utils";
import { GiftingBand } from "./GiftingBand";
import { GoalStatusSection } from "./GoalStatusSection";
import { HorizonCard } from "./HorizonCard";
import { GoalsOverviewRouteProps } from "./types";

const Masthead = styled.div`
  padding: 32px ${home.pagePad} 24px;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const BackPill = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  align-self: flex-start;
  padding: 0 14px 0 10px;
  border: 1px solid ${home.line12};
  border-radius: 999px;
  background: none;
  font-family: ${home.sans};
  font-size: 13px;
  color: ${home.ink62};
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill09};
  }
`;

const MastheadRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
`;

const Name = styled.h1`
  margin: 0;
  font-family: ${home.serif};
  font-size: 44px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.6px;
  color: ${home.ink};

  @media (max-width: 720px) {
    font-size: 34px;
  }
`;

const HorizonSection = styled.div`
  padding: 0 ${home.pagePad};
`;

const Footer = styled.div`
  padding: 52px ${home.pagePad} 46px;

  > div {
    padding-top: 26px;
    border-top: 1px solid ${home.line08};
    display: flex;
    align-items: flex-start;
    gap: 40px;
  }
`;

const Disclaimer = styled.div`
  font-size: 12px;
  line-height: 1.65;
  color: ${home.ink34};
  max-width: 800px;
`;

const FooterMark = styled.div`
  opacity: 0.5;
  margin-left: auto;
  transform: scale(0.78);
  transform-origin: right top;

  svg {
    display: block;
  }
`;

export const GoalsOverviewRoute = () => {
  const location = useLocation();

  // Without state (e.g. arriving from a marketing card) the context falls
  // back to the first beneficiary.
  const beneficiaryId = (location.state as GoalsOverviewRouteProps)?.beneficiaryId;

  return (
    <GoalsContextProvider beneficiaryId={beneficiaryId}>
      <GoalsOverviewRouteComponent />
    </GoalsContextProvider>
  );
};

export const GoalsOverviewRouteComponent = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  useGoalDetailsPrefetch();

  const { user, loading: userLoading, error: userError } = useUser();
  const { beneficiary, beneficiaryId, numberOfYearsActive } = useGoalsContext();

  const {
    data: performance,
    loading: performanceLoading,
    error: performanceError,
  } = usePortfolioPerformanceGraph({
    variables: {
      beneficiaryId,
      nodeCount: NODE_COUNT,
    },
  });

  const loading = performanceLoading || userLoading;
  const error = performanceError || userError;

  const goals = (beneficiary?.goals ?? []).filter((goal): goal is Goal => Boolean(goal));

  const standings = goals.map(deriveGoalStanding);

  const maxGoalLength = calculateMaxGoalLength(beneficiary?.goals);
  const annualDeposit = calculateTotalAnnualBeneficiaryDeposit(beneficiary?.goals);
  const multiplier = calculateMaxMultiplier(beneficiary?.goals);
  const contributeAfter18 = calculateContributeAfter18Value(beneficiary?.goals);

  const name =
    (beneficiary?.type === BeneficiaryType.Self
      ? user?.preferredName || user?.firstName
      : beneficiary?.firstName) ||
    i18n.t(Translations.HomeYou) ||
    "";

  const sinceYear = (() => {
    const years = standings.map((s) => s.startYear).filter((year) => Number.isFinite(year));
    return years.length ? Math.min(...years) : null;
  })();

  const monthly = Math.round(annualDeposit / 12);
  const interest = Math.round((multiplier - 1) * 100);

  return (
    <Page style={homeCssVars(theme.meta.dark)}>
      <Grain aria-hidden="true" />
      <Content>
        <AppNav />

        {loading ? (
          <Masthead>
            <Loader />
          </Masthead>
        ) : error ? (
          <Masthead>
            <ErrorComponent />
          </Masthead>
        ) : (
          <>
            <Masthead>
              <BackPill onClick={() => navigate(Routes.Accounts)}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 6-6 6 6 6" />
                </svg>
                {i18n.t(Translations.GoalsOverviewRouteAllAccounts)}
              </BackPill>

              <MastheadRow>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Eyebrow>
                    {sinceYear
                      ? i18n.t(Translations.GoalsOverviewRouteSince, {
                          year: sinceYear,
                        })
                      : i18n.t(Translations.GoalsOverviewRouteBeneficiary)}
                  </Eyebrow>
                  <Name>{name}</Name>
                </div>

                <GhostButton
                  style={{ marginLeft: "auto", flexShrink: 0 }}
                  onClick={() => navigate(Routes.RequestGift, { state: { beneficiaryId } })}
                >
                  {i18n.t(Translations.RequestGiftRouteCTA)}
                </GhostButton>
              </MastheadRow>
            </Masthead>

            <HorizonSection>
              <HorizonCard
                performance={performance}
                standings={standings}
                annualDeposit={annualDeposit}
                multiplier={multiplier}
                contributeAfter18={contributeAfter18}
                numberOfYearsActive={numberOfYearsActive}
                maxGoalLength={maxGoalLength}
              />
            </HorizonSection>

            <GoalStatusSection standings={standings} name={name} />

            <GiftingBand name={name} />

            <Footer>
              <div>
                <Disclaimer>
                  {i18n.t(Translations.GoalsOverviewRouteDisclosure, {
                    monthly: formatCurrency(monthly),
                    rate: interest,
                  })}
                </Disclaimer>
                <FooterMark>
                  <FutureMoneyLogo />
                </FooterMark>
              </div>
            </Footer>
          </>
        )}
      </Content>
    </Page>
  );
};
